import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Alert, Button } from "react-bootstrap";
import Loader from "src/components/common/loader/loader";
import { SftpService } from "@services/ingest/sftpService";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";

const service = new SftpService();

// The chrome-less App shell is a fixed-height, overflow-hidden flex column, which would
// clip a printout to one page. Undo that for print, and hide the toolbar.
const STYLES = `
.sftp-instructions-scroll { flex: 1 1 auto; overflow-y: auto; }
.sftp-instructions { max-width: 820px; margin: 0 auto; padding: 24px 32px 48px; background: #fff; font-size: 15px; line-height: 1.6; color: #222; }
.sftp-instructions h1 { font-size: 1.6rem; margin-bottom: 16px; }
.sftp-instructions h2 { font-size: 1.25rem; margin-top: 32px; }
.sftp-instructions h3 { font-size: 1.05rem; margin-top: 24px; }
.sftp-instructions pre { background: #f6f4fd; padding: 12px; border-radius: 4px; overflow-x: auto; }
.sftp-instructions code { font-family: monospace; font-size: 14px; }
.sftp-instructions li { margin-bottom: 4px; }
@media print {
  html, body, #root, .vh-100, .sftp-instructions-scroll { height: auto !important; overflow: visible !important; }
  .sftp-instructions-toolbar { display: none; }
  .sftp-instructions { padding: 0; }
}
`;

/** The customer SFTP setup guide, rendered from the server's markdown (MPN-2082). Opens
 * chrome-less so "Print / Save as PDF" gives the customer a file they can forward to
 * their IT or Slate administrator, who usually has no platform login. */
export default function SftpInstructionsPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useDocumentTitle("SFTP setup instructions");

  useEffect(() => {
    if (!organizationId) return;
    service
      .getInstructionsMarkdown(Number(organizationId))
      .then((result) => setMarkdown(result.markdown))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load the SFTP instructions"));
  }, [organizationId]);

  if (error) return <Alert variant="danger" className="m-4">{error}</Alert>;
  if (markdown === null) return <Loader />;

  return (
    <div className="sftp-instructions-scroll">
      <style>{STYLES}</style>
      <div className="sftp-instructions">
        <div className="sftp-instructions-toolbar d-flex justify-content-end mb-3">
          <Button size="sm" variant="outline-primary" onClick={() => window.print()}>
            <i className="bi bi-printer me-1" />
            Print / Save as PDF
          </Button>
        </div>
        <ReactMarkdown>{markdown}</ReactMarkdown>
      </div>
    </div>
  );
}
